import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';
import { ResponsiveContainer } from 'recharts'; // still usable as a wrapper

import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import { TooltipIcon } from '../../../shared-components/tooltip/TooltipIcon';
import { fillMissingMonthsGeneric } from '../../../shared-functions/ChartFunctions';
import HexToRgba from '../../../shared-functions/ConvertHexToRGB';
import accountApi from '../../../utils/api/account';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string; // month/day/hour
  totalRevenue: number;
  totalAmountPaid: number;
  totalPayableAmount: number;
  monthName: string;
}

/**
 * Revenue vs Payable vs Net Payable vs Payout Chart
 */
function RevenueVsPayableChart() {
  const { t, i18n } = useTranslation('account');
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const accountCustomId = id || searchParameters.get('accountCustomId');
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary-color')
    .trim();
  const [graphData, setGraphData] = useState<ChartDataProperties[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        rtl: i18n.language === 'ar',
        labels: {
          /**
           * hides all legend items (no “radio buttons”)
           * @returns {any} \
           */
          filter: () => false,
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
        rtl: i18n.language === 'ar',
        textDirection: i18n.language === 'ar' ? 'rtl' : 'ltr',
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        reverse: i18n.language === 'ar',
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false }, //  remove Y grid lines
        border: { display: false }, //  remove Y axis line
        ticks: { display: false }, //  remove Y axis numbers
      },
    },
  };

  /** Draws the vertical crosshair line in black on hover, overriding the default dataset color */
  const verticalHoverLinePlugin = {
    id: 'verticalHoverLine',
    /**
     * Draws a black vertical crosshair line at the hovered x position.
     * @param {any} chart - The Chart.js instance
     */
    afterDraw(chart: any) {
      if (chart.tooltip?._active?.length) {
        const context = chart.ctx as CanvasRenderingContext2D;
        const { x } = chart.tooltip._active[0].element;
        const { top, bottom } = chart.chartArea;

        const successDark = getComputedStyle(document.documentElement)
          .getPropertyValue('--success')
          .trim();

        context.save();
        context.beginPath();
        context.moveTo(x, top);
        context.lineTo(x, bottom);
        context.lineWidth = 1;
        context.strokeStyle = successDark;
        context.stroke();
        context.restore();
      }
    },
  };

  /**
   * Fetches the revenue vs payable report data for the specified time range and updates the chart.
   */
  const fetchReport = async () => {
    if (!accountCustomId) {
      return;
    }
    try {
      const response = await accountApi.fetchRevenuePayable(accountCustomId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      const { data } = response;

      const formattedData: ChartDataProperties[] = fillMissingMonthsGeneric(data).map((d: any) => ({
        label: d.label,
        totalRevenue: d.totalRevenue ?? 0,
        totalAmountPaid: d.totalAmountPaid ?? 0,
        totalPayableAmount: d.totalPayableAmount ?? 0,
        monthName: d.monthName ?? d.label,
      }));

      setGraphData(formattedData);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * Month translation mapping
   */

  const monthMap: Record<string, string> = {
    Jan: 'jan',
    Feb: 'feb',
    Mar: 'mar',
    Apr: 'apr',
    May: 'may_month',
    Jun: 'jun',
    Jul: 'jul',
    Aug: 'aug',
    Sep: 'sep',
    Oct: 'oct',
    Nov: 'nov',
    Dec: 'dec',
  };

  useEffect(() => {
    fetchReport();
  }, [accountCustomId, i18n.language, dateRange.startDate, dateRange.endDate]);

  /**
   * Translate month labels
   */
  const labels = graphData.map((d) => {
    const monthLabel = (d.label || d.monthName || '') as string;
    const parts = monthLabel.split(' ');

    if (parts.length === 2 && monthMap[parts[0]]) {
      return `${t(monthMap[parts[0]])} ${parts[1]}`;
    }

    return monthLabel;
  });

  return (
    <div className="power-consumed-chart">
      <div className="power-consumed-container">
        <div className="report-header header">
          <div className="report-title">
            <div className="title-wrapper">
              <p>
                {t('revenue')} {t('vs')} {t('payable')} {t('vs')} {t('payout')}
              </p>
              <TooltipIcon text="Revenue vs Payable vs Payout" infoColor="info" />
            </div>
            <CustomDateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
              placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
              defaultDuration="last7Days"
            />
          </div>
        </div>
        <div className="power-consumed-container">
          <ResponsiveContainer width="100%" height={220}>
            <Line
              data={{
                labels,
                datasets: [
                  {
                    label: t('revenue'),
                    data: graphData.map((d) => d.totalRevenue),
                    borderColor: primaryColor,
                    backgroundColor: HexToRgba(primaryColor, 0.2),
                    borderWidth: 1.5,
                    fill: false,
                    tension: 0.4,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                  },
                  {
                    label: t('payable'),
                    data: graphData.map((d) => d.totalPayableAmount),
                    borderColor: '#6c5ce7',
                    backgroundColor: 'rgba(108, 92, 231, 0.2)',
                    fill: false,
                    tension: 0.4,
                    borderWidth: 1.5,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                  },
                  {
                    label: t('payout'),
                    data: graphData.map((d) => d.totalAmountPaid),
                    borderColor: '#d63031',
                    backgroundColor: 'rgba(214, 48, 49, 0.2)',
                    fill: false,
                    tension: 0.4,
                    borderWidth: 1.5,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                  },
                ],
              }}
              options={chartOptions}
              plugins={[verticalHoverLinePlugin]}
            />
          </ResponsiveContainer>
        </div>

        <div className="chart-label">
          <div className="each-label">
            <div className="each-label-color" style={{ backgroundColor: primaryColor }} />
            <p>{t('revenue')}</p>
          </div>
          <div className="each-label">
            <div className="each-label-color" style={{ backgroundColor: '#5D5DE4' }} />
            <p>{t('payable')}</p>
          </div>

          <div className="each-label">
            <div className="each-label-color" style={{ backgroundColor: '#F87171' }} />
            <p>{t('payout')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RevenueVsPayableChart;
